import { Button, message } from "antd";
import {
  ParcelCardWrapperTable,
  ParcelHeadWrapper,
  ParcelRightIconWrapper,
  ParcelWrapperAll,
} from "components/ManageParcel/manageParcelLayout";
import { useFactoryCreditTopups } from "hooks/useFactoryCreditTopups";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFactoryCreditTopups } from "services/factoryCreditApi";
import CreditTopupListTable from "./CreditTopupListTable";
import CreditTransactionSearchForm from "./CreditTransactionSearchForm";
import { EMPTY_CREDIT_SEARCH, CreditSearchDraft } from "./creditTransactionOptions";
import { exportCreditTopupsExcel } from "./exportCreditTopupsExcel";

/**
 * ตาราง + ค้นหา + export ของแท็บ "จัดการเครดิต" (2026-09-02) — มุมมองเน้น "การสั่งซื้อ" ตามที่ขอ
 * (เลขที่ทำรายการ/วันที่/package/ผู้ทำรายการ/ช่องทาง/สถานะ) คนละ query/table/export กับ
 * CreditHistorySection (มุมมอง ledger ทั่วไป ใช้แท็บ "ประวัติการใช้เครดิต" อยู่ ไม่แตะเลย) — reuse แค่
 * CreditTransactionSearchForm (hideSystemType+hideType เหลือแค่ค้นหา+ช่วงวันที่ ใช้ query ตรงกันได้พอดี)
 * และ layout wrapper เดิม (ParcelWrapperAll ฯลฯ) ให้หน้าตาเหมือนแท็บข้างกัน
 */
const CreditTopupHistorySection: FC = () => {
  const navigate = useNavigate();
  const [applied, setApplied] = useState<CreditSearchDraft>(EMPTY_CREDIT_SEARCH);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [exporting, setExporting] = useState(false);

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      date_from: applied.date_from || undefined,
      date_to: applied.date_to || undefined,
      keyword: applied.keyword || undefined,
    }),
    [page, limit, applied]
  );

  const { data, isFetching } = useFactoryCreditTopups(queryParams);

  const handleSearch = (next: CreditSearchDraft) => {
    setApplied(next);
    setPage(1);
  };

  const handleClear = () => {
    setApplied(EMPTY_CREDIT_SEARCH);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getFactoryCreditTopups({ ...queryParams, page: 1, export: true });
      if (!res.data?.length) {
        message.info("ไม่มีรายการตามตัวกรองนี้");
        return;
      }
      if (res.truncated) {
        message.warning("ส่งออกได้สูงสุด 5,000 รายการตามตัวกรองนี้ — รายการที่เกินจะไม่ถูกรวม");
      }
      exportCreditTopupsExcel(res.data);
    } catch (e) {
      console.error(e);
      message.error("ส่งออก Excel ไม่สำเร็จ");
    } finally {
      setExporting(false);
    }
  };

  return (
    <ParcelWrapperAll>
      <ParcelHeadWrapper>
        <CreditTransactionSearchForm
          applied={applied}
          onSearch={handleSearch}
          onClear={handleClear}
          hideSystemType
          hideType
        />
        <ParcelRightIconWrapper>
          <Button type="default" onClick={handleExport} loading={exporting}>
            Export Excel
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: 8 }}
            onClick={() => navigate("/manage-credit/topup")}
          >
            เติมเครดิต
          </Button>
        </ParcelRightIconWrapper>
      </ParcelHeadWrapper>

      <ParcelCardWrapperTable>
        <CreditTopupListTable
          data={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={page}
          limit={limit}
          onPageChange={(nextPage, nextSize) => {
            setPage(nextPage);
            setLimit(nextSize);
          }}
        />
      </ParcelCardWrapperTable>
    </ParcelWrapperAll>
  );
};

export default CreditTopupHistorySection;
